import classNames from "classnames";
import React, { memo, useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { BasketMenuIcon as BasketMenuIconComponent } from "tsi-common-react/src/apps/checkout/BasketMenuIcon/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { CountdownTimer } from "tsi-common-react/src/components/CountdownTimer/index";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RichNavigation } from "tsi-common-react/src/components/RichNavigation/index";
import { useMobileMenuDialog } from "tsi-common-react/src/hooks/useMobileMenuDialog";
import { ICountdownTimer } from "tsi-common-react/src/models/countdown-timer";
import {
    WebPageURL,
    isoWebPageURL,
} from "tsi-common-react/src/models/nominals";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import {
    isPageInsideIFrame as isPageInsideIFrameCheck,
    usePageSetting,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL, useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context, reverse } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import cocoonLogoWithoutStitch from "../../../static/img/global/cocoon-logo-without-stitch.svg";
import cocoonLogo from "../../../static/img/global/cocoon-logo.svg";
import { FinancingModalTriggerMinicart } from "../../apps/checkout/FinancingModalTriggerMinicart";
import { getNavHeight } from "../../utils/nav-measurements";
import { PromoCopyStreamField } from "../PromoCopyStreamField";

import styles from "./index.module.scss";

interface IBasketMenuIconProps {
    basketURL: WebPageURL;
    checkoutURL: WebPageURL;
    isPageInsideIFrame: boolean;
    enableBasketPrequalSDK: boolean;
    isSSR: boolean;
}

interface ISecondaryNavProps extends IBasketMenuIconProps {
    ordersURL: WebPageURL;
}

const Logo = memo(
    ({ screen, homeURL }: { screen: string; homeURL: WebPageURL }) => (
        <div
            className={classNames("logo-container", {
                [styles.logoContainerDesktop]: screen === "--desktop",
                [styles.logoContainerMobile]: screen === "--mobile",
            })}
        >
            {/* TODO(i18n): Use t tagged template: title={t`Cocoon Home Page`} */}
            <Link className="logo" href={homeURL} title="Cocoon Home Page">
                <picture>
                    {/* TODO(i18n): Use t tagged template: title={t`Cocoon By Sealy`} */}
                    <SVG
                        className="header-brand-logo"
                        src={cocoonLogo}
                        title="Cocoon By Sealy"
                    />
                    {/* TODO(i18n): Use t tagged template: title={t`Cocoon By Sealy`} */}
                    <SVG
                        className="header-brand-logo--mobile"
                        src={cocoonLogoWithoutStitch}
                        title="Cocoon By Sealy"
                    />
                </picture>
            </Link>
        </div>
    ),
);

const BasketMenuIcon = memo(
    ({
        basketURL,
        checkoutURL,
        isPageInsideIFrame,
        enableBasketPrequalSDK,
        isSSR,
    }: IBasketMenuIconProps) => (
        <BasketMenuIconComponent
            basketURL={basketURL}
            checkoutURL={checkoutURL}
            isPageInsideIFrame={isPageInsideIFrame}
            basketLineVariant={BasketLineVariant.MINIMAL_ENHANCED}
            addPopperArrow={true}
            buildPromoComponents={(closeModal) => {
                if (!enableBasketPrequalSDK) {
                    return null;
                }
                return (
                    <FinancingModalTriggerMinicart closeModal={closeModal} />
                );
            }}
            isSSR={isSSR}
        />
    ),
);

const SecondaryNav = memo(
    ({ ordersURL, ...otherProps }: ISecondaryNavProps) => {
        const staticURL = useStaticURL();
        return (
            <div className={styles.secondaryNav}>
                {/* TODO(i18n): Use t tagged template: title={t`Cocoon by Sealy Orders`} */}
                <Link href={ordersURL} title="Cocoon by Sealy Orders">
                    {/* TODO(i18n): Use t tagged template: alt={t`Account`} */}
                    <img
                        src={`${staticURL}img/icons/account-icon.svg`}
                        alt="Account"
                    />
                </Link>
                <div className="site-nav__main-menu-cart site-nav__secondary-menu__item">
                    <BasketMenuIcon {...otherProps} />
                </div>
            </div>
        );
    },
);

type ISiteHeaderProps = React.ContextType<
    typeof Context
>["chrome"]["site_header"];

// Mutable reactivated props (not Immutable<>): this template spreads
// rich_navigation into the shared RichNavigation component, which is typed with
// mutable reactivated props; an Immutable<> spread would not be assignable.
export const SiteHeader = (props: ISiteHeaderProps) => {
    const isSSR = useIsSSR();
    const staticURL = useStaticURL();
    const viewport = useCurrentBreakpoint();
    const [timerReady, setTimerReady] = useState(false);
    // `scrolled` / `isMobile` drive `#aux-header`'s appearance classes. They are
    // React state (not imperative classList writes) because React owns the
    // header's className -- an imperative add would be dropped on the next
    // render. Both start false so the SSR markup and first client render match;
    // the scroll/resize effects below flip them post-hydration.
    const [scrolled, setScrolled] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const {
        rich_navigation,
        is_enabled,
        timer_queue,
        promotions,
        promo_page_url,
        promo_image,
    } = props;
    const {
        siteHeaderRef,
        mobilePanelVisible,
        showMobileMenuDialog,
        openMobilePanel,
        closeMobilePanel,
    } = useMobileMenuDialog({
        belowMobile: viewport.belowMobile,
        hasRichNavigation: !!rich_navigation,
        // cocoon's mobile nav is a genuine one-at-a-time accordion whose
        // aria-expanded/aria-hidden React owns; leave them intact.
        stripAccordionAttributes: false,
    });
    const promoCopy = promotions?.promo_copy?.value ?? [];
    const hasPromoCopy = promoCopy.length > 0;

    const homeURL = usePageURL("home");
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const offersURL = usePageURL("offers_link");
    const ordersURL = isoWebPageURL.wrap("/orders/");
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();
    const enableBasketPrequalSDK = usePageSetting("show_prequal_cart") || false;
    const mobilePanelOpen = showMobileMenuDialog;

    const preHeaderClasses = classNames({
        "pre-header": true,
        "pre-header--timer-present": is_enabled,
        "pre-header--timer-ready": is_enabled && timerReady,
        "pre-header--promo-copy": !is_enabled && !timerReady,
        "pre-header--hidden": !is_enabled && !hasPromoCopy,
    });
    const auxHeaderClasses = classNames({
        "aux-header--timer-present": is_enabled,
        "scrolled": scrolled,
        "mobile": isMobile,
    });

    const richNavigationClasses = classNames({
        "rich-navigation": true,
        "is-visible": mobilePanelOpen,
    });

    // As the StremFields converted into strings
    // Rename the fields for aliasing and type compatibility
    const timersConvertedType = timer_queue?.timers?.map((time) => {
        const { body_str, mobile_body_str, timer_theme, ...rest } = time;
        return {
            ...rest,
            body: body_str,
            mobile_body: mobile_body_str,
            theme: timer_theme,
        } as ICountdownTimer;
    });

    // Spread props to the child components
    const basketMenuIconProps = {
        basketURL,
        checkoutURL,
        isPageInsideIFrame,
        enableBasketPrequalSDK,
        isSSR,
    };
    const secondaryNavProps = {
        ordersURL,
        ...basketMenuIconProps,
    };

    // Lock body scroll while the mobile drawer is open. cocoon's `.modal-open`
    // rule only raises the header z-index; it does not lock scroll, so this is
    // kept even though the dialog hook also toggles `.modal-open`.
    useEffect(() => {
        const document = getDocument();
        if (!document || !mobilePanelOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            if (document.body.style.overflow === "hidden") {
                document.body.style.overflow = previousOverflow;
            }
        };
    }, [mobilePanelOpen]);

    // Sticky-on-scroll appearance for `#aux-header`. Desktop (>767px) toggles
    // the `scrolled` class once scrolled past the nav height; on mobile the
    // viewport inner height (sans the iOS bottom bar) is published as
    // `--docInnerHeight` so the absolutely-positioned rich-nav drawer can size
    // to it.
    useEffect(() => {
        const window = getWindow();
        const document = getDocument();
        if (!window || !document) {
            return;
        }
        const onScroll = () => {
            if (window.innerWidth > 767) {
                const scrollPos =
                    window.pageYOffset ||
                    document.documentElement.scrollTop ||
                    document.body.scrollTop ||
                    0;
                setScrolled(scrollPos > getNavHeight());
            } else {
                document.documentElement.style.setProperty(
                    "--docInnerHeight",
                    `${window.innerHeight}px`,
                );
            }
        };
        window.addEventListener("scroll", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    // Track the mobile breakpoint for `#aux-header`'s `mobile` class (and the
    // initial `--docInnerHeight`). Only width changes matter -- mobile URL-bar
    // show/hide changes innerHeight but not innerWidth, and reacting to that
    // would thrash the class.
    useEffect(() => {
        const window = getWindow();
        const document = getDocument();
        if (!window || !document) {
            return;
        }
        let lastInnerWidth = window.innerWidth;
        const applyMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) {
                document.documentElement.style.setProperty(
                    "--docInnerHeight",
                    `${window.innerHeight}px`,
                );
            }
        };
        applyMobile();
        const onResize = () => {
            if (lastInnerWidth === window.innerWidth) {
                return;
            }
            lastInnerWidth = window.innerWidth;
            applyMobile();
        };
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return (
        <>
            <div className={preHeaderClasses}>
                <a
                    className="aux-header__screen-reader-skip"
                    href="#main-content"
                >
                    {t`Skip to main content`}
                </a>
                {is_enabled ? (
                    // TODO(i18n): Use t tagged template for aria-label: aria-label={t`Sale countdown`}
                    <div
                        className="aux-header__timer"
                        role="region"
                        aria-label="Sale countdown"
                    >
                        <div className="countdown-timer-container">
                            <CountdownTimer
                                timerData={timersConvertedType}
                                timersExpiredPromo={
                                    hasPromoCopy ? (
                                        <PromoCopyStreamField
                                            value={promoCopy}
                                        />
                                    ) : (
                                        ""
                                    )
                                }
                                onTimerReady={() => setTimerReady(true)}
                            />
                        </div>
                    </div>
                ) : (
                    hasPromoCopy && (
                        <div className="aux-header__copy">
                            <PromoCopyStreamField value={promoCopy} />
                        </div>
                    )
                )}
                {promo_page_url && !is_enabled && (
                    <div
                        className={classNames(
                            "shop-container",
                            styles.shopContainer,
                        )}
                    >
                        <a
                            href={promo_page_url}
                            className="button shop-button al-header__shop-button"
                            id="shop-btn-value-props"
                        >
                            {t`Shop`}
                        </a>
                    </div>
                )}
                {!rich_navigation && (
                    <div className="mobile-header-content">
                        <div className="brand brand--mobile">
                            {/* TODO(i18n): Use t tagged template: title={t`Go to home page`} */}
                            <Link href={homeURL} title="Go to home page">
                                <picture>
                                    <source
                                        media={`(max-width: 768px)`}
                                        srcSet={`${staticURL}img/global/cocoon-logo.svg`}
                                    />
                                    {/* TODO(i18n): Use t tagged template: alt={t`Cocoon stitch logo`} (also fix typo: "log" -> "logo") */}
                                    <Image
                                        src={`${staticURL}img/global/logo-cocoon-stitch.png`}
                                        alt={`Cocoon stitch log`}
                                    />
                                </picture>
                            </Link>
                        </div>
                        <div
                            className={classNames(
                                "site-nav__cart-container",
                                styles.siteNavCartContainer,
                            )}
                        >
                            <div className="site-nav__main-menu-cart site-nav__secondary-menu__item">
                                <BasketMenuIcon {...basketMenuIconProps} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <header
                id="aux-header"
                className={auxHeaderClasses}
                ref={siteHeaderRef}
                role={showMobileMenuDialog ? "dialog" : undefined}
                aria-modal={showMobileMenuDialog ? true : undefined}
                aria-labelledby={
                    showMobileMenuDialog ? "mobile-panel-title" : undefined
                }
            >
                {showMobileMenuDialog && (
                    <span
                        id="mobile-panel-title"
                        className="ada-screenreader-only"
                    >
                        {t`Menu`}
                    </span>
                )}
                <div className={classNames("nav-header", styles.navHeader)}>
                    {/** if Rich Navigation is configured, render this */}
                    {rich_navigation && (
                        <div className="main-container">
                            <button
                                className="mobile-toggle"
                                aria-controls="mobile-panel"
                                aria-expanded={
                                    mobilePanelVisible ? "true" : "false"
                                }
                                onClick={
                                    mobilePanelVisible
                                        ? closeMobilePanel
                                        : (event) =>
                                              openMobilePanel(
                                                  event.currentTarget,
                                              )
                                }
                            >
                                {mobilePanelVisible ? (
                                    // TODO(i18n): Use t tagged template: alt={t`Close icon`}
                                    <img
                                        src={`${staticURL}img/global/hamburger-close.svg`}
                                        alt="Close icon"
                                    />
                                ) : (
                                    // TODO(i18n): Use t tagged template: alt={t`Hamburger navigation icon`}
                                    <img
                                        src={`${staticURL}img/global/hamburger.svg`}
                                        alt="Hamburger navigation icon"
                                    />
                                )}
                            </button>
                            <Logo screen="--desktop" homeURL={homeURL} />
                            {(showMobileMenuDialog ||
                                !viewport.belowMobile) && (
                                <div
                                    id="mobile-panel"
                                    className={richNavigationClasses}
                                >
                                    <RichNavigation
                                        {...rich_navigation}
                                        openMobileContentDefaultState={false}
                                        enableMobileAccordion={true}
                                    />
                                </div>
                            )}
                            <Logo screen="--mobile" homeURL={homeURL} />
                            {promo_image ? (
                                <>
                                    {/* TODO(i18n): Use t tagged template: title={t`Cocoon by Sealy Offers`} */}
                                    <Link
                                        href={offersURL}
                                        title="Cocoon by Sealy Offers"
                                    >
                                        {/* TODO(i18n): Use t tagged template: alt={t`Navigation promotional image`} */}
                                        <img
                                            src={promo_image}
                                            alt="nav-promo-image"
                                        />
                                    </Link>
                                    <SecondaryNav {...secondaryNavProps} />
                                </>
                            ) : (
                                <SecondaryNav {...secondaryNavProps} />
                            )}
                        </div>
                    )}
                </div>
            </header>
        </>
    );
};
